import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHolidays } from '../../../entities/holiday';
import { type DeliveryEstimate, deliveryEstimates } from './model';

/** The current time, refreshed every minute so an open form moves past the cut-off by itself. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

export type DescribedEstimate = DeliveryEstimate & {
  /** "Order before 12:00 PM Toronto time (9:00 AM your time) today." */
  cutoffText: string;
  /** "Delivered by Oct 8, 2026, 2:30 PM" */
  deliveredText: string;
};

/**
 * The delivery estimate of each turnaround in the viewer's language and time zone, keyed by its number of
 * business days. Empty until the holidays have loaded. `timeZone` and `now` exist for tests.
 */
export function useDeliveryEstimates(
  turnarounds: readonly number[],
  { timeZone, now: fixedNow }: { timeZone?: string; now?: Date } = {},
): Map<number, DescribedEstimate> {
  const { t, i18n } = useTranslation('order');
  const holidays = useHolidays();
  const clock = useNow();
  const now = fixedNow ?? clock;
  return useMemo(() => {
    if (!holidays.data) return new Map();
    return new Map(
      deliveryEstimates({
        now,
        holidays: holidays.data,
        turnarounds,
        locale: i18n.language,
        ...(timeZone ? { timeZone } : {}),
      }).map((e) => {
        const values = { time: e.cutoffTime, local: e.localCutoffTime, day: e.cutoffDay };
        const cutoffText = e.cutoffIsToday
          ? e.localCutoffTime
            ? t('estimate.cutoffTodayLocal', values)
            : t('estimate.cutoffToday', values)
          : e.localCutoffTime
            ? t('estimate.cutoffLaterLocal', values)
            : t('estimate.cutoffLater', values);
        return [
          e.turnaroundDays,
          { ...e, cutoffText, deliveredText: t('estimate.delivered', { when: e.deliveredBy }) },
        ];
      }),
    );
  }, [holidays.data, now, turnarounds, i18n.language, timeZone, t]);
}
